import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {computed, inject, Injectable, Signal} from '@angular/core';
import {StorageKeys} from '@core/storage';
import {localStorageSignal} from '@ddtmm/angular-signal-generators';
import {environment} from '@shared-types';
import {Observable} from 'rxjs';
import {HttpRequestConfig, HttpShortHeaders, HttpShortParams} from '../http-types';

const apiUrlLocalStorageSignal$ = localStorageSignal<string>(undefined, StorageKeys.AuthKeys.KeyAPIUri);

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	public static apiVersion = '1.0';
	public static appVersion = '1.0';
	/* -------------------------------------------------------------------------------------------------------------- */
	private readonly http: HttpClient = inject(HttpClient);
	/* -------------------------------------------------------------------------------------------------------------- */
	public readonly apiURL: Signal<string> = computed(() => apiUrlLocalStorageSignal$() || environment.apiURL);

	/* -------------------------------------------------------------------------------------------------------------- */
	constructor() {}

	private getApiUrl(path: string): string {
		const apiUrl = this.apiURL() || environment.apiURL;
		const baseURL = new URL(apiUrl);
		const url = `${baseURL.origin}/api/${path}`;

		return url;
	}

	public defaultHeaders: HttpHeaders = new HttpHeaders({
		'Content-Type': 'application/json',
		'Api-Version': ApiService.apiVersion,
		'App-Version': ApiService.appVersion,
		Accept: `application/vnd.doccycle.v${ApiService.apiVersion}+json, application/json, text/plain, */*`,
	});

	getHeaders(headers: HttpShortHeaders | HttpHeaders = this.defaultHeaders): HttpHeaders {
		return headers instanceof HttpHeaders ? headers : new HttpHeaders(headers);
	}

	/* -------------------------------------------------------------------------------------------------------------- */
	/**
	 * Sends a GET request to the specified path.
	 * @template ResponseType - The expected response type.
	 * @param {string} path - The endpoint path.
	 * @param {HttpShortParams} params - The request parameters.
	 * @param {HttpShortHeaders} headers - The request headers.
	 * @param {boolean} appendToken - Whether to append the token to the request.
	 * @returns {Observable<ResponseType>} - The response as an observable.
	 */
	get<ResponseType>(path: string, params: HttpShortParams = new HttpParams(), headers: HttpShortHeaders = this.getHeaders(), appendToken = true): Observable<ResponseType> {
		const url: string = this.getApiUrl(path);
		const config: HttpRequestConfig = new HttpRequestConfig(headers, params, appendToken);
		return this.http.get<ResponseType>(url, config.httpConfig);
	}

	/**
	 * Sends a POST request to the specified path.
	 * @template ResponseType - The expected response type.
	 * @template BodyType - The type of the request body.
	 * @param {string} path - The endpoint path.
	 * @param {BodyType} body - The request body.
	 * @param {HttpShortHeaders} headers - The request headers.
	 * @param params
	 * @param {boolean} appendToken - Whether to append the token to the request.
	 * @returns {Observable<ResponseType>} - The response as an observable.
	 */
	post<ResponseType, BodyType>(path: string, body: BodyType, headers: HttpShortHeaders = this.getHeaders(), params: HttpShortParams | object = {}, appendToken = true): Observable<ResponseType> {
		const url: string = this.getApiUrl(path);
		const bodyString: string = typeof body === 'string' ? body : JSON.stringify(body);
		const shortParams: HttpShortParams = params as HttpShortParams;
		if (!(params instanceof HttpParams)) {
			Object.keys(params).forEach(key => {
				shortParams[key] = params[key];
			});
		}
		const config: HttpRequestConfig = new HttpRequestConfig(headers, shortParams, appendToken);
		return this.http.post<ResponseType>(url, bodyString, config.httpConfig);
	}

	/**
	 * Sends a PUT request to the specified path.
	 * @template ResponseType - The expected response type.
	 * @template BodyType - The type of the request body.
	 * @param {string} path - The endpoint path.
	 * @param {BodyType} body - The request body.
	 * @param {HttpShortHeaders} headers - The request headers.
	 * @param {boolean} appendToken - Whether to append the token to the request.
	 * @returns {Observable<ResponseType>} - The response as an observable.
	 */
	put<ResponseType, BodyType>(path: string, body: BodyType, headers: HttpShortHeaders = this.getHeaders(), appendToken = true): Observable<ResponseType> {
		const url: string = this.getApiUrl(path);
		const bodyString: string = typeof body === 'string' ? body : JSON.stringify(body);
		const config: HttpRequestConfig = new HttpRequestConfig(headers, null, appendToken);
		return this.http.put<ResponseType>(url, bodyString, config.httpConfig);
	}

	/**
	 * Sends a DELETE request to the specified path.
	 * @template ResponseType - The expected response type.
	 * @param {string} path - The endpoint path.
	 * @param {HttpShortParams} params - The request parameters.
	 * @param {HttpShortHeaders} headers - The request headers.
	 * @param {boolean} appendToken - Whether to append the token to the request.
	 * @returns {Observable<ResponseType>} - The response as an observable.
	 */
	delete<ResponseType>(path: string, params: HttpShortParams = new HttpParams(), headers: HttpShortHeaders = this.getHeaders(), appendToken = true): Observable<ResponseType> {
		const url: string = this.getApiUrl(path);
		const config: HttpRequestConfig = new HttpRequestConfig(headers, params, appendToken);
		return this.http.delete<ResponseType>(url, config.httpConfig);
	}

	/**
	 * Sends a PATCH request to the specified path.
	 * @template ResponseType - The expected response type.
	 * @template BodyType - The type of the request body.
	 * @param {string} path - The endpoint path.
	 * @param {BodyType} body - The request body.
	 * @param {HttpShortHeaders} headers - The request headers.
	 * @param {boolean} appendToken - Whether to append the token to the request.
	 * @returns {Observable<ResponseType>} - The response as an observable.
	 */
	patch<ResponseType, BodyType>(path: string, body: BodyType, headers: HttpShortHeaders = this.getHeaders(), appendToken = true): Observable<ResponseType> {
		const url: string = this.getApiUrl(path);
		const bodyString: string = typeof body === 'string' ? body : JSON.stringify(body);
		const config: HttpRequestConfig = new HttpRequestConfig(headers, null, appendToken);
		return this.http.patch<ResponseType>(url, bodyString, config.httpConfig);
	}
}
